import { useFragment } from '@apollo/client';
import { useMemo } from 'react';
import { gql } from 'src/gql';
import {
  Address,
  Conversation,
  isClientDeleted, mapClient,
  useAddress,
  useDeleteClient,
  useLabelToCta,
  useRestoreClient,
} from 'src/graphql';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { DEFAULT_PAGINATION_LIMIT } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods } from 'src/store/modals';
import type { ClientRaw } from 'src/types';
import {
  useConfirmationModal,
  useCopyToClipboard,
  useEntityFullUrl,
  useGetTosUrl,
} from 'src/utils/hooks';

const UseClientActionsFragment = gql(`
  fragment UseClientActions_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    deletedAt
    notes
    marketingOptOut
    primaryEmail {
      id
      value
      labelId
    }
    primaryPhoneNumber {
      id
      value
      labelId
    }
    emails {
      id
      clientEmailId
      value
      labelId
    }
    phoneNumbers {
      id
      clientPhoneNumberId
      value
      labelId
    }
  }
`);

export const useClientActions = ({
  clientId,
  address,
  conversation,
}: {
  clientId: string;
  address?: Partial<Address>;
  conversation?: Conversation;
}) => {
  const { data: client } = useFragment({
    fragment: UseClientActionsFragment,
    fragmentName: 'UseClientActions_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const { tosUrl } = useGetTosUrl({ partnerId: client.partnerId, brandId: address?.brandId });
  const { confirm } = useConfirmationModal();
  const { updateAddress, deleteClientToAddress } = useAddress(address?.addressId);
  const { deleteLabelToCta } = useLabelToCta();
  const { deleteClient } = useDeleteClient();
  const { restoreClient } = useRestoreClient();
  const { groupRules } = useGroupRules();
  const { copyToClipboard } = useCopyToClipboard();
  const { showModal: showAddClientModal } = useModalMethods(Modals.AddClientModal);
  const { showModal: showMergeClientModal } = useModalMethods(Modals.MergeClientModal);

  const clientFullUrl = useEntityFullUrl(client.partnerId, EntityNames.Client, clientId);

  // TODO(gql-migrate): AddClientModal and MergeClientModal still consume the legacy mapped
  // Client shape; drop this bridge once they read the cache via useFragment themselves.
  const legacyClient = useMemo(
    () => mapClient(client as ClientRaw),
    [client],
  );

  const baseFilters: ConditionInput[] = useMemo(() => {
    const filters: ConditionInput[] = [];
    if (address?.addressId) {
      filters.push({
        attribute: 'addresses/addressId',
        operator: '=',
        value: address?.addressId,
      });
    }
    return filters;
  }, [address?.addressId]);

  return useMemo(
    () => ({
      updateClient: {
        label: 'Update client',
        icon: 'edit',
        isAllowed: !isClientDeleted(client),
        handler: () => {
          showAddClientModal({ client: legacyClient, address });
        },
      },
      copyClientId: {
        label: 'Copy Client ID',
        icon: 'content_copy',
        handler: async () => {
          await copyToClipboard(clientId);
        },
      },
      copyClientUrl: {
        label: 'Copy Client URL',
        icon: 'link',
        isAllowed: !!clientFullUrl,
        handler: async () => {
          await copyToClipboard(clientFullUrl);
        },
      },
      copyTOS: {
        label: 'Copy Terms of Service URL',
        icon: 'link',
        isAllowed: !!tosUrl,
        handler: async () => {
          await copyToClipboard(tosUrl);
        },
      },
      disassociateClient: {
        label: 'Disassociate Client',
        icon: 'delete',
        isAllowed: !!address?.addressId,
        handler: async () => {
          if (
            await confirm({
              body: 'Please confirm you want to disassociate \nthe client from the site.',
              variant: 'delete',
            })
          ) {
            if (address?.primaryClientId === clientId) {
              await updateAddress(
                {
                  ...address,
                  primaryClientId: null,
                  primaryClient: {
                    clientId: null,
                  },
                },
                conversation?.conversationId,
              );
            }
            await deleteLabelToCta({
              clientId,
              addressId: address?.addressId,
            });
            await deleteClientToAddress(
              clientId,
              address?.partner?.partnerId ?? address?.partnerId,
            );
          }
        },
      },
      mergeClients: {
        label: 'Merge clients',
        icon: 'merge',
        isAllowed: !isClientDeleted(client),
        handler: () => {
          showMergeClientModal({
            client: legacyClient,
            filters: baseFilters,
            limit: DEFAULT_PAGINATION_LIMIT,
          });
        },
      },
      deleteClient: {
        label: 'Delete client',
        icon: 'delete',
        isAllowed:
          !!groupRules[UserGroupRules.AccessToDeleteClients] && !isClientDeleted(client),
        handler: async () => {
          if (
            await confirm({
              body: 'Please confirm you want to delete this client.\nThe client and its contact details will be removed but can be restored.',
              variant: 'delete',
            })
          ) {
            await deleteClient(clientId);
          }
        },
      },
      restoreClient: {
        label: 'Restore client',
        icon: 'restore_from_trash',
        isAllowed:
          isClientDeleted(client) && !!groupRules[UserGroupRules.AccessToRestoreClients],
        handler: async () => {
          await restoreClient(clientId);
        },
      },
    }),
    [
      clientFullUrl,
      tosUrl,
      address,
      showAddClientModal,
      client,
      clientId,
      legacyClient,
      copyToClipboard,
      confirm,
      deleteLabelToCta,
      deleteClientToAddress,
      updateAddress,
      conversation?.conversationId,
      showMergeClientModal,
      baseFilters,
      deleteClient,
      restoreClient,
      groupRules,
    ],
  );
};
